import React, { useState, useEffect } from 'react';

const VideoRebrand = ({ user }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('medium');
  const [customOptions, setCustomOptions] = useState({});
  const [presets, setPresets] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [userStats, setUserStats] = useState({
    totalRebrands: 0,
    avgProcessingTime: '~45s',
    recentFiles: []
  });

  // Load presets on component mount
  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      const response = await fetch('/api/rebrand-presets');
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets);
      } else {
        throw new Error('Failed to load presets');
      }
    } catch (error) {
      console.error('Error loading presets:', error);
      // Fallback to default presets
      const defaultPresets = {
        light: {
          name: "Light Modifications",
          description: "Very subtle changes - hardest to detect",
          brightness: 0.03,
          contrast: 1.02,
          saturation: 1.01,
          scaleRatio: 0.999,
          grainOpacity: 0.02
        },
        medium: {
          name: "Medium Modifications", 
          description: "Balanced modifications (recommended)",
          brightness: 0.05,
          contrast: 1.05,
          saturation: 1.03,
          scaleRatio: 0.997,
          grainOpacity: 0.04
        },
        heavy: {
          name: "Heavy Modifications",
          description: "More noticeable but still natural",
          brightness: 0.08,
          contrast: 1.08,
          saturation: 1.05,
          scaleRatio: 0.995,
          grainOpacity: 0.06
        },
        custom: {
          name: "Custom Settings",
          description: "User-defined modifications"
        }
      };
      setPresets(defaultPresets);
    }
  };

  const validateVideoFile = (file) => {
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-flv'];
    const maxSize = 500 * 1024 * 1024; // 500MB
    
    if (!file || !validTypes.includes(file.type)) {
      return { valid: false, error: 'Please select a valid video file (MP4, MOV, AVI, WebM, FLV)' };
    }
    
    if (file.size > maxSize) {
      return { valid: false, error: 'File size must be less than 500MB' };
    }
    
    return { valid: true };
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validation = validateVideoFile(file);
      if (validation.valid) {
        setSelectedFile(file);
        setError(null);
        
        // Update recent files
        const newFile = {
          name: file.name,
          size: (file.size / 1024 / 1024).toFixed(2),
          timestamp: new Date().toISOString()
        };
        
        setUserStats(prev => ({
          ...prev,
          recentFiles: [newFile, ...prev.recentFiles.slice(0, 4)]
        }));
      } else {
        setError(validation.error);
        setSelectedFile(null);
      }
    }
  };

  const handleRebrand = async () => {
    if (!selectedFile) {
      setError('Please select a video file');
      return;
    }

    const validation = validateVideoFile(selectedFile);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setError(null);
    setIsProcessing(true);
    setProgress(0);
    setResult(null);
    setJobId(null);

    try {
      await processVideoOnServer(selectedFile);
    } catch (error) {
      console.error('Rebrand failed:', error);
      setError(error.message);
      setIsProcessing(false);
      setProgress(0);
      setJobId(null);
    }
  };

  const processVideoOnServer = async (file) => {
    setProgress(5);
    
    // Create FormData for file upload
    const formData = new FormData();
    formData.append('video', file);
    formData.append('preset', selectedPreset);
    formData.append('userId', user?.uid || 'anonymous');
    
    // Add custom options if using custom preset
    if (selectedPreset === 'custom') {
      formData.append('customOptions', JSON.stringify(customOptions));
    }

    setProgress(10);

    try {
      // Submit job to server
      const response = await fetch('/api/rebrand-video', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const data = await response.json();
      setJobId(data.jobId);
      setProgress(15);

      // Poll for job completion
      await pollJobStatus(data.jobId);
      
    } catch (error) {
      console.error('Server processing error:', error);
      throw error;
    }
  };

  const pollJobStatus = async (jobId) => {
    const pollInterval = 2000; // Poll every 2 seconds
    const maxAttempts = 180; // Max 6 minutes
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(`/api/rebrand-status/${jobId}`);
        
        if (!response.ok) {
          throw new Error(`Status check failed: ${response.status}`);
        }

        const status = await response.json();
        
        // Update progress
        if (status.progress !== undefined) {
          setProgress(Math.min(status.progress, 95));
        }

        if (status.status === 'completed') {
          setProgress(100);
          setResult({
            originalName: selectedFile.name,
            modifiedName: status.result.filename,
            downloadUrl: status.result.downloadUrl,
            firebaseUrl: status.result.firebaseResult?.videoUrl, // Store Firebase URL
            preset: selectedPreset,
            modifications: status.result.modifications,
            codec: status.result.codec,
            framerate: status.result.framerate,
            processedAt: new Date().toISOString()
          });
          
          // Update stats
          setUserStats(prev => ({
            ...prev,
            totalRebrands: prev.totalRebrands + 1
          }));
          
          setIsProcessing(false);
          return;
        }

        if (status.status === 'failed') {
          throw new Error(status.error || 'Processing failed on server');
        }

        // Continue polling if still processing
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          throw new Error('Processing timeout - please try again');
        }
        
      } catch (error) {
        console.error('Polling error:', error);
        setError(error.message);
        setIsProcessing(false);
        setProgress(0);
      }
    };

    poll();
  };

  const handleDownload = async () => {
    if (result?.downloadUrl) {
      try {
        const response = await fetch(result.downloadUrl);
        const blob = await response.blob();
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.modifiedName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Download failed:', error);
        setError('Failed to download file. Please try again.');
      }
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setSelectedPreset('medium');
    setCustomOptions({});
    setIsProcessing(false);
    setProgress(0);
    setJobId(null);
    setResult(null);
    setError(null);
    setShowAdvanced(false);
    
    // Reset file input
    const fileInput = document.getElementById('video-file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const handleRecentFileClick = (fileName) => {
    setError('Please select the file again from your computer');
  };

  const updateCustomOption = (key, value) => {
    setCustomOptions(prev => ({
      ...prev,
      [key]: parseFloat(value) || 0
    }));
  };

  return (
    <div className="dashboard-twitter-importer">
      {/* Preset Selection Modal */}
      {showPresetModal && (
        <div className="audio-modal-overlay" onClick={() => setShowPresetModal(false)}>
          <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚙️ Choose Preset</h3>
              <button 
                className="modal-close"
                onClick={() => setShowPresetModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="audio-options">
              {Object.entries(presets).filter(([key]) => key !== 'custom').map(([key, preset]) => (
                <div 
                  key={key}
                  className={`audio-option ${selectedPreset === key ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedPreset(key);
                    setShowPresetModal(false);
                  }}
                >
                  <div className="audio-icon">
                    {key === 'light' ? '🟢' : key === 'medium' ? '🟡' : '🔴'}
                  </div>
                  <div className="audio-details">
                    <div className="audio-name">{preset.name}</div>
                    <div className="audio-description">{preset.description}</div>
                  </div>
                  <div className="audio-radio">
                    <input 
                      type="radio" 
                      name="preset-selection" 
                      checked={selectedPreset === key}
                      onChange={() => setSelectedPreset(key)}
                    />
                  </div>
                </div>
              ))}
              
              <div 
                className={`audio-option ${selectedPreset === 'custom' ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedPreset('custom');
                  setShowAdvanced(true);
                  setShowPresetModal(false);
                }}
              >
                <div className="audio-icon">⚙️</div>
                <div className="audio-details">
                  <div className="audio-name">Custom Settings</div>
                  <div className="audio-description">Configure your own parameters</div>
                </div>
                <div className="audio-radio">
                  <input 
                    type="radio" 
                    name="preset-selection" 
                    checked={selectedPreset === 'custom'}
                    onChange={() => setSelectedPreset('custom')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Settings Modal */}
      {showAdvanced && selectedPreset === 'custom' && (
        <div className="audio-modal-overlay" onClick={() => setShowAdvanced(false)}>
          <div className="audio-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚙️ Custom Settings</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAdvanced(false)}
              >
                ×
              </button>
            </div>
            
            <div className="custom-controls" style={{ padding: '20px' }}>
              <div className="control-row" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
                  Brightness Adjustment: {((customOptions.brightness || 0.05) * 100).toFixed(1)}%
                </label>
                <input
                  type="range"
                  min="0.01"
                  max="0.15"
                  step="0.01"
                  value={customOptions.brightness || 0.05}
                  onChange={(e) => updateCustomOption('brightness', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="control-row" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
                  Contrast Adjustment: {((customOptions.contrast || 1.05) * 100).toFixed(1)}%
                </label>
                <input
                  type="range"
                  min="0.85"
                  max="1.15"
                  step="0.01"
                  value={customOptions.contrast || 1.05}
                  onChange={(e) => updateCustomOption('contrast', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="control-row" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
                  Saturation Adjustment: {((customOptions.saturation || 1.03) * 100).toFixed(1)}%
                </label>
                <input
                  type="range"
                  min="0.90"
                  max="1.10"
                  step="0.01"
                  value={customOptions.saturation || 1.03}
                  onChange={(e) => updateCustomOption('saturation', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="control-row" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
                  Film Grain Intensity: {((customOptions.grainOpacity || 0.04) * 100).toFixed(1)}%
                </label>
                <input
                  type="range"
                  min="0.01"
                  max="0.10"
                  step="0.01"
                  value={customOptions.grainOpacity || 0.04}
                  onChange={(e) => updateCustomOption('grainOpacity', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="control-row" style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#fff' }}>
                  Scale Ratio: {((customOptions.scaleRatio || 0.997) * 100).toFixed(2)}%
                </label>
                <input
                  type="range"
                  min="0.990"
                  max="1.010"
                  step="0.001"
                  value={customOptions.scaleRatio || 0.997}
                  onChange={(e) => updateCustomOption('scaleRatio', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-confirm"
                onClick={() => setShowAdvanced(false)}
                style={{ 
                  background: '#4ecdc4', 
                  color: '#000', 
                  border: 'none', 
                  padding: '10px 20px', 
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Apply Settings
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        <div className="content-header">
          <h1 className="page-title gradient-title">🎬 Video Rebrand</h1>
          <p className="page-description">Upload and modify TikTok videos with real FFmpeg processing</p>
        </div>

        {!isProcessing && !result && (
          <>
            <div className="import-section">
              <div className="section-card">
                <div className="card-header">
                  <h3 className="card-title">📁 Upload Video</h3>
                  <p className="card-description">Select a TikTok video file to rebrand with real effects</p>
                </div>
                
                <div className="import-form">
                  <div className="form-group">
                    <label className="form-label">Video File (Max 500MB)</label>
                    <div className="input-group">
                      <div className="file-upload-container">
                        <input
                          id="video-file-input"
                          type="file"
                          accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-flv"
                          onChange={handleFileSelect}
                          className="file-input"
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="video-file-input" className="form-input file-input-label">
                          {selectedFile ? (
                            <div className="file-selected-display">
                              <span className="file-icon">🎬</span>
                              <div className="file-info">
                                <div className="file-name">{selectedFile.name}</div>
                                <div className="file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                              </div>
                            </div>
                          ) : (
                            <div className="file-placeholder-display">
                              <span className="upload-icon">📁</span>
                              <span>Click to select video file</span>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {/* Preset Selection Button */}
                      <button
                        className="audio-button"
                        onClick={() => setShowPresetModal(true)}
                        disabled={isProcessing}
                        title="Choose Preset"
                      >
                        <span className="audio-icon">⚙️</span>
                      </button>
                      
                      <button
                        onClick={handleRebrand}
                        disabled={!selectedFile || isProcessing}
                        className={`import-button ${isProcessing ? 'loading' : ''}`}
                      >
                        <span className="button-icon">🎭</span>
                        Process Video
                      </button>
                    </div>

                    {/* Preset Selection Display */}
                    {selectedPreset && presets[selectedPreset] && (
                      <div className="audio-selection-display">
                        <span className="audio-indicator">
                          ⚙️ Preset: {presets[selectedPreset].name}
                        </span>
                        <button 
                          className="change-audio-btn"
                          onClick={() => setShowPresetModal(true)}
                        >
                          Change
                        </button>
                        {selectedPreset === 'custom' && (
                          <button 
                            className="change-audio-btn"
                            onClick={() => setShowAdvanced(true)}
                            style={{ marginLeft: '8px' }}
                          >
                            Customize
                          </button>
                        )}
                      </div>
                    )}
                    
                    {error && (
                      <div className="error-alert">
                        <span className="alert-icon">⚠️</span>
                        <span>{error}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="stats-section">
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">🎭</div>
                  <div className="stat-content">
                    <div className="stat-number">{userStats.totalRebrands}</div>
                    <div className="stat-label">Videos Rebranded</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">⚡</div>
                  <div className="stat-content">
                    <div className="stat-number">60fps</div>
                    <div className="stat-label">Output Framerate</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🔧</div>
                  <div className="stat-content">
                    <div className="stat-number">H.264</div>
                    <div className="stat-label">Output Codec</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="recent-section">
              <div className="section-card">
                <div className="card-header">
                  <h3 className="card-title">🎬 Recent Files</h3>
                  <p className="card-description">Your last 5 processed videos</p>
                </div>
                
                <div className="recent-list">
                  {userStats.recentFiles.length > 0 ? (
                    userStats.recentFiles.map((item, index) => (
                      <div key={index} className="recent-item">
                        <div className="recent-icon">🎬</div>
                        <div className="recent-content">
                          <div className="recent-url">{item.name}</div>
                          <div className="recent-time">{item.size} MB • {formatTimeAgo(item.timestamp)}</div>
                        </div>
                        <button 
                          className="recent-action" 
                          title="Select this file again"
                          onClick={() => handleRecentFileClick(item.name)}
                        >
                          ↻
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <p>No recent files yet. Start by uploading your first video!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {isProcessing && (
          <div className="import-section">
            <div className="section-card">
              <div className="card-header">
                <h3 className="card-title">🔄 Processing Video</h3>
                <p className="card-description">Applying {presets[selectedPreset]?.name || selectedPreset} modifications with FFmpeg</p>
              </div>
              
              <div className="processing-content">
                <div className="processing-header">
                  <div className="processing-details">
                    <div>File: <span className="file-preview">{selectedFile?.name}</span></div>
                    <div>Preset: <strong>{presets[selectedPreset]?.name || selectedPreset}</strong></div>
                    {jobId && <div>Job ID: <code style={{ fontSize: '12px', opacity: 0.7 }}>{jobId}</code></div>}
                  </div>
                </div>

                <div className="progress-container">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <div className="progress-text">{progress}%</div>
                </div>

                <div className="processing-steps">
                  <div className={`step ${progress >= 10 ? 'completed' : progress >= 5 ? 'active' : ''}`}>
                    📤 Uploading to server
                  </div>
                  <div className={`step ${progress >= 30 ? 'completed' : progress >= 15 ? 'active' : ''}`}>
                    🎬 Analyzing video properties
                  </div>
                  <div className={`step ${progress >= 60 ? 'completed' : progress >= 30 ? 'active' : ''}`}>
                    🎨 Applying FFmpeg effects
                  </div>
                  <div className={`step ${progress >= 85 ? 'completed' : progress >= 60 ? 'active' : ''}`}>
                    🏷️ Adding metadata & grain
                  </div>
                  <div className={`step ${progress >= 100 ? 'completed' : progress >= 85 ? 'active' : ''}`}>
                    ✅ Finalizing 60fps output
                  </div>
                </div>

                <div className="estimated-time">
                  Server processing: 1-3 minutes depending on file size
                </div>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="import-section">
            <div className="section-card">
              <div className="card-header">
                <h3 className="card-title">✅ Rebrand Complete!</h3>
                <p className="card-description">Your video has been processed with real FFmpeg effects</p>
              </div>
              
              <div className="result-content">
                <div className="result-header">
                  <div className="result-details">
                    <div>Original: <strong>{result?.originalName}</strong></div>
                    <div>Modified: <strong>{result?.modifiedName}</strong></div>
                    <div>Preset: <strong>{presets[selectedPreset]?.name || selectedPreset}</strong></div>
                    <div>Codec: <strong>{result?.codec || 'H.264'}</strong></div>
                    <div>Framerate: <strong>{result?.framerate || '60fps'}</strong></div>
                  </div>
                </div>

                <div className="modifications-applied">
                  <h4>🛠️ Real Modifications Applied:</h4>
                  <ul className="modification-list">
                    <li>🎬 Reprocessed with FFmpeg at 60fps</li>
                    <li>🎨 Applied color grading (brightness, contrast, saturation)</li>
                    <li>✨ Added film grain overlay</li>
                    <li>📏 Subtle scaling and unsharp mask</li>
                    <li>🏷️ Updated metadata with Premiere Pro signature</li>
                    <li>🔄 H.264 codec with optimized settings</li>
                    <li>⚡ Enhanced for platform distribution</li>
                  </ul>
                </div>

                <div className="result-actions">
                  {result?.firebaseUrl ? (
                    <button 
                      onClick={() => window.open(result.firebaseUrl, '_blank')}
                      className="import-button"
                    >
                      <span className="button-icon">🔗</span>
                      Open Firebase Link
                    </button>
                  ) : (
                    <button 
                      onClick={handleDownload}
                      className="import-button"
                    >
                      <span className="button-icon">📥</span>
                      Download Rebranded Video
                    </button>
                  )}
                  
                  <button 
                    onClick={handleReset}
                    className="import-button secondary"
                  >
                    <span className="button-icon">🔄</span>
                    Process Another Video
                  </button>
                </div>

                <div className="success-tips">
                  <h4>💡 Processing Details:</h4>
                  <ul>
                    <li>Video processed server-side with real FFmpeg effects</li>
                    <li>Output codec: {result?.codec || 'H.264'} at {result?.framerate || '60fps'}</li>
                    <li>Metadata spoofed to match Premiere Pro export</li>
                    <li>All modifications applied to avoid detection algorithms</li>
                    <li>Professional-grade video processing pipeline</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoRebrand; 